import { useCallback, useEffect } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { currentFamilyIdAtom, memberLabelsAtom } from '../store';
import { api } from '../lib/api';

// What you call each family member: Dadi, Nani, Bua… (docs/indian-touches.md,
// D143). Yours alone, shown in place of their name wherever you see it.

/** Loads the current family's labels; mounted once in the app shell. */
export function useMemberLabelsSync() {
  const familyId = useAtomValue(currentFamilyIdAtom);
  const [, setLabels] = useAtom(memberLabelsAtom);
  useEffect(() => {
    setLabels({});
    if (!familyId) return;
    let cancelled = false;
    api
      .getMemberLabels(familyId)
      .then((response) => {
        if (!cancelled) setLabels(response.data);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [familyId, setLabels]);
}

export function useMemberLabels() {
  const familyId = useAtomValue(currentFamilyIdAtom);
  const [labels, setLabels] = useAtom(memberLabelsAtom);
  const setLabel = useCallback(
    async (memberId: string, label: string) => {
      if (!familyId) return;
      const response = await api.setMemberLabel(familyId, memberId, label);
      setLabels((current) => {
        const next = { ...current };
        if (response.data.label) next[memberId] = response.data.label;
        else delete next[memberId];
        return next;
      });
    },
    [familyId, setLabels]
  );
  return { labels, setLabel };
}
